/**
 * Motion primitives.
 *
 * Rule 1 of DESIGN-SYSTEM §5: only `transform` and `opacity` animate. State-to-
 * state movement therefore runs on CSS transitions (GPU-composited, no main-thread
 * work); rAF is reserved for the two things CSS cannot express — typing, and
 * anything tracking a finger.
 */

export const EASE = 'cubic-bezier(.22,1,.36,1)';
/** DESIGN-SYSTEM §5 `--ease-in-out`: things moving between states — the camera. */
export const EASE_INOUT = 'cubic-bezier(.65,0,.35,1)';

let reduced: boolean | null = null;

/** Cached because it is read on every step change. */
export function prefersReducedMotion(): boolean {
  if (reduced === null) {
    const mq = globalThis.matchMedia?.('(prefers-reduced-motion: reduce)');
    reduced = mq?.matches ?? false;
    mq?.addEventListener?.('change', (e) => {
      reduced = e.matches;
    });
  }
  return reduced;
}

/** Duration honouring the reduced-motion preference. */
export function dur(ms: number): number {
  return prefersReducedMotion() ? 0 : ms;
}

export interface TweenHandle {
  cancel(): void;
}

/** rAF tween with an easing function. Returns a handle so callers can cancel. */
export function tween(
  ms: number,
  onFrame: (t: number) => void,
  onDone?: () => void,
  ease: (t: number) => number = easeOutQuint,
): TweenHandle {
  if (ms <= 0) {
    onFrame(1);
    onDone?.();
    return { cancel() {} };
  }
  let raf = 0;
  let cancelled = false;
  const start = performance.now();
  // Elapsed time is read from the same clock the tween started on, not from
  // the frame's own timestamp argument: the two share an origin in browsers,
  // but a test's fake frame clock need not, and a tween that finishes on its
  // first frame under test says nothing about how it behaves on a phone.
  const step = () => {
    if (cancelled) return;
    const t = Math.min(1, (performance.now() - start) / ms);
    onFrame(ease(t));
    if (t < 1) raf = requestAnimationFrame(step);
    else onDone?.();
  };
  raf = requestAnimationFrame(step);
  return {
    cancel() {
      cancelled = true;
      cancelAnimationFrame(raf);
    },
  };
}

export const easeOutQuint = (t: number): number => 1 - Math.pow(1 - t, 5);
export const linear = (t: number): number => t;

/**
 * Force a style flush so a freshly-inserted element animates from its initial
 * state instead of jumping. Cheaper and more reliable than a double rAF.
 */
export function reflow(el: HTMLElement): void {
  void el.offsetWidth;
}
